import React from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "@/lib/CartContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";

export default function CartDrawer() {
  const { cart, open, setOpen, updateItem, removeItem, checkout } = useCart();

  const onCheckout = async () => {
    try {
      const res = await checkout();
      if (!res) return;
      if (res.mode === "live") {
        window.location.href = res.checkout_url;
      } else {
        setOpen(false);
        window.location.href = res.checkout_url;
      }
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 bg-[#faf9f5] border-l border-[color:var(--line)]" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-5 border-b border-[color:var(--line)] flex flex-row items-center justify-between">
          <SheetTitle className="label !text-[11px]">Your bag {cart?.count ? `(${cart.count})` : ""}</SheetTitle>
          <SheetDescription className="sr-only">Review the items in your Vitura bag and proceed to checkout.</SheetDescription>
          <button onClick={() => setOpen(false)} aria-label="Close cart" data-testid="cart-close"><X size={16} /></button>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-180px)] overflow-y-auto">
          {(!cart || cart.lines.length === 0) && (
            <div className="p-10 text-center">
              <p className="editorial text-2xl mb-4">Your bag is empty.</p>
              <Link to="/preview/shop" onClick={() => setOpen(false)} className="label underline" data-testid="cart-empty-shop">Start with a flavor</Link>
            </div>
          )}
          {cart?.lines?.map((line) => (
            <div key={line.variant_id + line.subscribe} className="flex gap-4 px-6 py-5 border-b border-[color:var(--line)]" data-testid={`cart-line-${line.variant_id}`}>
              <img src={line.image} alt={line.flavor} className="w-20 h-24 object-contain bg-[#f1efe7]" />
              <div className="flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm">{line.product_title}</div>
                    <div className="label !text-[10px] mt-0.5">{line.flavor} · {line.variant_title}</div>
                    {line.subscribe && <div className="label !text-[10px] mt-1 text-[color:var(--accent-kelp)]">Subscribe & Save · 15% off</div>}
                  </div>
                  <button onClick={() => removeItem(line.variant_id)} className="label !text-[10px] underline" data-testid={`cart-remove-${line.variant_id}`}>Remove</button>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex items-center border border-[color:var(--line)]">
                    <button onClick={() => updateItem(line.variant_id, Math.max(0, line.quantity - 1))} className="w-8 h-8 flex items-center justify-center" data-testid={`cart-dec-${line.variant_id}`}><Minus size={12} /></button>
                    <span className="w-8 text-center text-sm">{line.quantity}</span>
                    <button onClick={() => updateItem(line.variant_id, line.quantity + 1)} className="w-8 h-8 flex items-center justify-center" data-testid={`cart-inc-${line.variant_id}`}><Plus size={12} /></button>
                  </div>
                  <div className="text-sm">${line.line_total.toFixed(2)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {cart?.lines?.length > 0 && (
          <div className="border-t border-[color:var(--line)] px-6 py-5 bg-[#faf9f5]">
            <div className="flex justify-between label mb-1"><span>Subtotal</span><span>${cart.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between label mb-4"><span>Shipping</span><span>{cart.shipping === 0 ? "Free" : `$${cart.shipping.toFixed(2)}`}</span></div>
            <button onClick={onCheckout} className="w-full h-12 bg-[#0f0f10] text-[#faf9f5] label !tracking-[0.25em] hover:bg-[#2a2a2a] transition" data-testid="cart-checkout">
              Checkout · ${cart.total.toFixed(2)}
            </button>
            <Link to="/preview/cart" onClick={() => setOpen(false)} className="block text-center label underline mt-3" data-testid="cart-view-full">View full bag</Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
